import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Search, LayoutGrid, List, Eye, Trash2, Star, Phone, Mail, ChevronDown, Users, UserCheck, UserX, Clock, Filter, Plus } from 'lucide-react';
import './RecruitmentDashboard.css';

const statusColors = {
    Applied: { bg: '#e0f2fe', color: '#0369a1' },
    Shortlisted: { bg: '#fef9c3', color: '#854d0e' },
    'Phone Screen': { bg: '#ede9fe', color: '#6d28d9' },
    Interview: { bg: '#dbeafe', color: '#1d4ed8' },
    Hired: { bg: '#dcfce7', color: '#166534' },
    Rejected: { bg: '#fee2e2', color: '#991b1b' },
};

const RecruitmentPipeline = () => {
    const [candidates, setCandidates] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [jobFilter, setJobFilter] = useState('All');
    const [viewMode, setViewMode] = useState('table');
    const [viewCandidate, setViewCandidate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const initialForm = { firstName: '', lastName: '', email: '', phone: '', jobId: '', status: 'Applied', rating: 0, notes: '', resumeUrl: '' };
    const [formData, setFormData] = useState(initialForm);
    const stages = ['Applied', 'Shortlisted', 'Phone Screen', 'Interview', 'Hired', 'Rejected'];

    useEffect(() => {
        fetchCandidates();
        fetchJobs();
    }, []);

    const fetchCandidates = async () => {
        try {
            const res = await api.get('/recruitment/candidates');
            setCandidates(res.data);
        } catch (err) { console.error("Failed to load candidates", err); }
        finally { setLoading(false); }
    };

    const fetchJobs = async () => {
        try { const res = await api.get('/recruitment/jobs'); setJobs(res.data); }
        catch (err) { console.error("Failed to load jobs", err); }
    };

    const handleStatusChange = async (candidateId, newStatus) => {
        setCandidates(prev => prev.map(c => c._id === candidateId ? { ...c, status: newStatus } : c));
        try { await api.patch(`/recruitment/candidates/${candidateId}/status`, { status: newStatus }); }
        catch (error) { console.error("Failed to update status", error); fetchCandidates(); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this candidate?')) return;
        try { await api.delete(`/recruitment/candidates/${id}`); fetchCandidates(); }
        catch (err) { alert('Failed to delete candidate'); }
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        if (!formData.jobId) { alert('Please select a job position.'); return; }
        setSubmitting(true);
        try {
            await api.post('/recruitment/candidates', formData);
            setShowAddModal(false);
            setFormData(initialForm);
            fetchCandidates();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to add application');
        } finally { setSubmitting(false); }
    };

    const handleDragStart = (e, candidateId) => e.dataTransfer.setData('candidateId', candidateId);
    const handleDragOver = (e) => e.preventDefault();
    const handleDrop = async (e, newStatus) => {
        const candidateId = e.dataTransfer.getData('candidateId');
        handleStatusChange(candidateId, newStatus);
    };

    const filtered = candidates.filter(c => {
        const name = `${c.firstName} ${c.lastName}`.toLowerCase();
        const matchSearch = name.includes(searchTerm.toLowerCase()) || (c.email || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === 'All' || c.status === statusFilter;
        const matchJob = jobFilter === 'All' || (c.jobId?._id === jobFilter || c.jobId === jobFilter);
        return matchSearch && matchStatus && matchJob;
    });

    const stats = {
        total: candidates.length,
        hired: candidates.filter(c => c.status === 'Hired').length,
        rejected: candidates.filter(c => c.status === 'Rejected').length,
        inProgress: candidates.filter(c => !['Hired', 'Rejected'].includes(c.status)).length,
    };

    const renderStars = (rating) => Array.from({ length: 5 }, (_, i) => (
        <Star key={i} size={12} fill={i < rating ? '#f59e0b' : 'none'} color={i < rating ? '#f59e0b' : '#d1d5db'} />
    ));

    if (loading) return (
        <div className="recruitment-container">
            {/* Header */}
            <div className="recruitment-header">
                <div>
                    <div className="skeleton skeleton-title" style={{ width: 180, marginBottom: 8 }} />
                    <div className="skeleton skeleton-text" style={{ width: 210 }} />
                </div>
            </div>

            {/* 4 Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                {[1,2,3,4].map(i => (
                    <div key={i} style={{ background: 'white', borderRadius: 10, padding: '16px 20px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div className="skeleton skeleton-text" style={{ width: 100, marginBottom: 8 }} />
                            <div className="skeleton" style={{ width: 52, height: 30, borderRadius: 6 }} />
                        </div>
                        <div className="skeleton" style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0 }} />
                    </div>
                ))}
            </div>

            {/* Filters bar */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', background: 'white', padding: '14px 18px', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <div className="skeleton" style={{ flex: 1, height: 36, borderRadius: 8 }} />
                <div className="skeleton" style={{ width: 160, height: 36, borderRadius: 8 }} />
                <div className="skeleton" style={{ width: 110, height: 36, borderRadius: 8 }} />
                <div className="skeleton" style={{ width: 72, height: 36, borderRadius: 8 }} />
            </div>

            {/* Table */}
            <div className="dark-card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '11px 14px', borderBottom: '1.5px solid #e8edf2', background: '#f8fafc', gap: 0 }}>
                    {[{ w: 170 }, { w: 120 }, { w: 140 }, { w: 80 }, { w: 100 }, { w: 80 }, { w: 65 }].map((col, i) => (
                        <div key={i} className="skeleton" style={{ height: 11, borderRadius: 4, width: col.w, marginRight: i < 6 ? 14 : 0, flexShrink: 0 }} />
                    ))}
                </div>
                {/* Data rows */}
                {[1,2,3,4,5,6].map(i => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #f1f5f9', gap: 0 }}>
                        {/* Candidate: avatar + name + email */}
                        <div style={{ width: 170, flexShrink: 0, marginRight: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
                            <div className="skeleton" style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }} />
                            <div>
                                <div className="skeleton skeleton-text" style={{ width: 90, marginBottom: 5 }} />
                                <div className="skeleton skeleton-text" style={{ width: 105 }} />
                            </div>
                        </div>
                        {/* Applied For */}
                        <div className="skeleton skeleton-text" style={{ width: 100, flexShrink: 0, marginRight: 14 }} />
                        {/* Contact: phone + email stacked */}
                        <div style={{ width: 140, flexShrink: 0, marginRight: 14 }}>
                            <div className="skeleton skeleton-text" style={{ width: '80%', marginBottom: 5 }} />
                            <div className="skeleton skeleton-text" style={{ width: '90%' }} />
                        </div>
                        {/* Rating: 5 stars */}
                        <div style={{ display: 'flex', gap: 3, width: 80, flexShrink: 0, marginRight: 14 }}>
                            {[1,2,3,4,5].map(s => <div key={s} className="skeleton" style={{ width: 12, height: 12, borderRadius: '50%' }} />)}
                        </div>
                        {/* Applied Date */}
                        <div className="skeleton skeleton-text" style={{ width: 80, flexShrink: 0, marginRight: 14 }} />
                        {/* Status badge */}
                        <div className="skeleton" style={{ width: 72, height: 22, borderRadius: 20, flexShrink: 0, marginRight: 14 }} />
                        {/* Actions: eye + trash */}
                        <div style={{ display: 'flex', gap: 6 }}>
                            <div className="skeleton" style={{ width: 28, height: 28, borderRadius: 6 }} />
                            <div className="skeleton" style={{ width: 28, height: 28, borderRadius: 6 }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="recruitment-container">
            {/* Header */}
            <div className="recruitment-header">
                <div>
                    <h2>Job Applications</h2>
                    <div className="breadcrumb"><span className="bc-parent">Recruit</span><span className="bc-sep"> • </span><span className="bc-current">Job Applications</span></div>
                </div>
                <button onClick={() => { setFormData(initialForm); setShowAddModal(true); }} className="rc-btn rc-btn-primary">
                    <Plus size={16} /> Add Application
                </button>
            </div>

            {/* Stats */}
            <div className="rc-stat-grid">
                {[
                    { label: 'Total Candidates', val: stats.total, color: '#6366f1', bg: '#eef2ff', icon: <Users size={20} /> },
                    { label: 'In Progress', val: stats.inProgress, color: '#f59e0b', bg: '#fffbeb', icon: <Clock size={20} /> },
                    { label: 'Hired', val: stats.hired, color: '#10b981', bg: '#ecfdf5', icon: <UserCheck size={20} /> },
                    { label: 'Rejected', val: stats.rejected, color: '#ef4444', bg: '#fff1f2', icon: <UserX size={20} /> },
                ].map((s, i) => (
                    <div key={i} className="rc-stat-card">
                        <div>
                            <div className="rc-stat-label">{s.label}</div>
                            <div className="rc-stat-value" style={{ color: s.color }}>{s.val}</div>
                        </div>
                        <div className="rc-stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
                    </div>
                ))}
            </div>

            {/* Filters & View Toggle */}
            <div className="rc-filters">
                <div className="rc-search-wrap">
                    <Search size={15} />
                    <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by name or email..." />
                </div>
                <select className="rc-select" style={{ minWidth: 160 }} value={jobFilter} onChange={e => setJobFilter(e.target.value)}>
                    <option value="All">All Jobs</option>
                    {jobs.map(j => <option key={j._id} value={j._id}>{j.title}</option>)}
                </select>
                <select className="rc-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="All">All Status</option>
                    {stages.map(s => <option key={s}>{s}</option>)}
                </select>
                <div className="rc-view-toggle">
                    <button onClick={() => setViewMode('table')} title="Table view" className={viewMode === 'table' ? 'active' : ''}><List size={16} /></button>
                    <button onClick={() => setViewMode('kanban')} title="Kanban view" className={viewMode === 'kanban' ? 'active' : ''}><LayoutGrid size={16} /></button>
                </div>
            </div>

            {/* Table View */}
            {viewMode === 'table' && (
                <div className="dark-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <table className="dark-table">
                        <thead>
                            <tr>
                                <th>Candidate</th>
                                <th>Applied For</th>
                                <th>Contact</th>
                                <th>Rating</th>
                                <th>Applied Date</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length > 0 ? filtered.map(c => {
                                const sc = statusColors[c.status] || { bg: '#f1f5f9', color: '#475569' };
                                return (
                                    <tr key={c._id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                                                    {c.firstName?.[0]?.toUpperCase()}{c.lastName?.[0]?.toUpperCase()}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{c.firstName} {c.lastName}</div>
                                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ color: '#475569', fontSize: 13 }}>{c.jobId?.title || '—'}</td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                {c.phone && <div style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={11} /> {c.phone}</div>}
                                                {c.email && <div style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} /> {c.email}</div>}
                                            </div>
                                        </td>
                                        <td><div style={{ display: 'flex', gap: 2 }}>{renderStars(c.rating || 0)}</div></td>
                                        <td style={{ fontSize: 13, color: '#475569' }}>{c.appliedDate ? new Date(c.appliedDate).toLocaleDateString() : new Date(c.createdAt).toLocaleDateString()}</td>
                                        <td>
                                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                                <select value={c.status} onChange={e => handleStatusChange(c._id, e.target.value)}
                                                    style={{ ...sc, padding: '4px 24px 4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}>
                                                    {stages.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                                <ChevronDown size={12} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: sc.color }} />
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <button onClick={() => setViewCandidate(c)} title="View" className="rc-icon-btn"><Eye size={14} /></button>
                                                <button onClick={() => handleDelete(c._id)} title="Delete" className="rc-icon-btn danger"><Trash2 size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }) : (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>No candidates found.</td></tr>
                            )}
                        </tbody>
                    </table>
                    <div className="rc-table-footer">
                        Showing {filtered.length} of {candidates.length} candidates
                    </div>
                </div>
            )}

            {/* Kanban View */}
            {viewMode === 'kanban' && (
                <div style={{ display: 'flex', overflowX: 'auto', gap: 16, paddingBottom: 20 }}>
                    {stages.map(stage => {
                        const sc = statusColors[stage] || { bg: '#f1f5f9', color: '#475569' };
                        const stageCandidates = filtered.filter(c => c.status === stage);
                        return (
                            <div key={stage} style={{ minWidth: 260, background: '#f8fafc', borderRadius: 10, padding: 14, border: '1px solid #e2e8f0', flexShrink: 0 }}
                                onDragOver={handleDragOver} onDrop={e => handleDrop(e, stage)}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                    <span style={{ ...sc, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{stage}</span>
                                    <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{stageCandidates.length}</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {stageCandidates.map(c => (
                                        <div key={c._id} draggable onDragStart={e => handleDragStart(e, c._id)}
                                            style={{ background: 'white', padding: 14, borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'grab', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
                                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                                            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 13 }}>{c.firstName} {c.lastName}</div>
                                                <button onClick={() => setViewCandidate(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2, display: 'flex' }}><Eye size={13} /></button>
                                            </div>
                                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{c.jobId?.title || 'No job'}</div>
                                            <div style={{ display: 'flex', gap: 1, marginTop: 8 }}>{renderStars(c.rating || 0)}</div>
                                        </div>
                                    ))}
                                    {stageCandidates.length === 0 && <div style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 12, padding: '20px 0' }}>Drop here</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add Application Modal */}
            {showAddModal && (
                <div className="rc-modal-overlay">
                    <div className="rc-modal wide">
                        <div className="rc-modal-header">
                            <h3 className="rc-modal-title">Add Job Application</h3>
                            <button className="rc-modal-close" onClick={() => setShowAddModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleAddSubmit}>
                            <div className="form-group">
                                <label>Applying For (Job) *</label>
                                <select name="jobId" required value={formData.jobId} onChange={e => setFormData({ ...formData, jobId: e.target.value })}>
                                    <option value="">Select a Job Position</option>
                                    {jobs.map(j => <option key={j._id} value={j._id}>{j.title}</option>)}
                                </select>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label>First Name *</label>
                                    <input name="firstName" required value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} placeholder="e.g. John" />
                                </div>
                                <div className="form-group">
                                    <label>Last Name *</label>
                                    <input name="lastName" required value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} placeholder="e.g. Doe" />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label>Email *</label>
                                    <input type="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="john@example.com" />
                                </div>
                                <div className="form-group">
                                    <label>Phone</label>
                                    <input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 98765 43210" />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label>Status</label>
                                    <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                                        {stages.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Rating (1–5)</label>
                                    <select value={formData.rating} onChange={e => setFormData({ ...formData, rating: Number(e.target.value) })}>
                                        <option value={0}>No rating</option>
                                        {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} star{n !== 1 ? 's' : ''}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Resume URL</label>
                                <input value={formData.resumeUrl} onChange={e => setFormData({ ...formData, resumeUrl: e.target.value })} placeholder="https://drive.google.com/..." />
                            </div>
                            <div className="form-group">
                                <label>Notes</label>
                                <textarea rows={3} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Any notes about this applicant..." />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                                <button type="button" onClick={() => setShowAddModal(false)} className="rc-btn rc-btn-secondary">Cancel</button>
                                <button type="submit" disabled={submitting} className="rc-btn rc-btn-primary">
                                    {submitting ? 'Adding...' : 'Add Application'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Candidate Modal */}
            {viewCandidate && (
                <div className="rc-modal-overlay">
                    <div className="rc-modal">
                        <div className="rc-modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16 }}>
                                    {viewCandidate.firstName?.[0]}{viewCandidate.lastName?.[0]}
                                </div>
                                <div>
                                    <h3 className="rc-modal-title">{viewCandidate.firstName} {viewCandidate.lastName}</h3>
                                    <div style={{ fontSize: 12, color: 'var(--rc-text-2)', marginTop: 2 }}>{viewCandidate.jobId?.title || 'No job'}</div>
                                </div>
                            </div>
                            <button className="rc-modal-close" onClick={() => setViewCandidate(null)}>×</button>
                        </div>
                        <div style={{ padding: 24 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                                {[
                                    { l: 'Email', v: viewCandidate.email },
                                    { l: 'Phone', v: viewCandidate.phone || '—' },
                                    { l: 'Status', v: viewCandidate.status },
                                    { l: 'Applied', v: viewCandidate.appliedDate ? new Date(viewCandidate.appliedDate).toLocaleDateString() : new Date(viewCandidate.createdAt).toLocaleDateString() },
                                ].map((f, i) => (
                                    <div key={i} style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{f.l}</div>
                                        <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>
                                            {f.l === 'Status' ? <span style={{ ...statusColors[f.v], padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{f.v}</span> : f.v}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Rating</div>
                                <div style={{ display: 'flex', gap: 3 }}>{renderStars(viewCandidate.rating || 0)}</div>
                            </div>
                            {viewCandidate.notes && (
                                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                                    <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
                                    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{viewCandidate.notes}</div>
                                </div>
                            )}
                            {viewCandidate.resumeUrl && (
                                <div style={{ marginBottom: 16 }}>
                                    <a href={viewCandidate.resumeUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', fontSize: 13, fontWeight: 600 }}>View Resume →</a>
                                </div>
                            )}
                            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, marginBottom: 8 }}>CHANGE STATUS</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {stages.map(s => {
                                        const sc = statusColors[s] || {};
                                        const isActive = viewCandidate.status === s;
                                        return (
                                            <button key={s} onClick={() => { handleStatusChange(viewCandidate._id, s); setViewCandidate({ ...viewCandidate, status: s }); }}
                                                style={{ padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: isActive ? 'none' : '1px solid #e2e8f0', background: isActive ? sc.bg : 'white', color: isActive ? sc.color : '#64748b' }}>
                                                {s}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecruitmentPipeline;
